import { QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { isApiError } from "@/api/types";

/**
 * Central React Query client.
 * - Query errors surface via toast unless the query opts out with meta.silent.
 * - Mutation errors are handled per-call (see feature hooks) so we don't
 *   double-toast; the global handler here is a safety net only.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: (failureCount, error) => {
        const status = isApiError(error) ? error.status : undefined;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
      onError: (error) => {
        const message = isApiError(error) ? error.message : error.message;
        toast.error(message ?? "Something went wrong. Please try again.");
      },
    },
  },
});

export const queryKeys = {
  auth: {
    me: ["auth", "me"] as const,
  },
  dashboard: {
    stats: ["dashboard", "stats"] as const,
    revenue: (range: string) => ["dashboard", "revenue", range] as const,
    activity: ["dashboard", "activity"] as const,
  },
} as const;
